import { useState, useEffect } from "react";
import "./index.css";

declare function gtag(...args: unknown[]): void;

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }
  return utm;
}

export function App() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);

  useEffect(() => {
    fetch("/api/waitlist/count")
      .then((res) => res.json())
      .then((data) => setWaitlistCount(data.count))
      .catch(() => {}); // silently hide on failure
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Something went wrong. Please try again.");
      }

      setStatus("success");
      // Fire Google Ads conversion event
      if (typeof gtag === "function") {
        gtag("event", "conversion", {
          send_to: "AW-17078120942/_LL8CPacnoMcEO7jvc8_",
          ...getUtmParams(),
        });
      }
      // Optimistic update: increment count locally
      setWaitlistCount((prev) => (prev !== null ? prev + 1 : prev));
    } catch (err) {
      setStatus("error");
      setErrorMessage(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Branded Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-2xl mx-auto px-6 flex items-center justify-between h-14">
          <a href="/" className="text-lg font-semibold tracking-tight text-foreground hover:text-accent transition-colors">
            Clerq
          </a>
          <a
            href="/guide/auto-enrolment-pension-ireland"
            className="text-sm text-accent hover:underline transition-colors"
          >
            Pension Auto-Enrolment Guide &rarr;
          </a>
        </div>
      </header>

      {/* Hero */}
      <main className="max-w-2xl mx-auto px-6 py-20 sm:py-32 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
          Irish Employment Compliance
          <br />
          <span className="text-muted-foreground">Made Simple</span>
        </h1>

        <p className="mt-6 text-lg text-muted-foreground max-w-xl mx-auto">
          Generate compliant employment contracts, staff handbooks, and compliance documents in minutes.
          Built for Irish employers. WRC-ready.
        </p>

        {/* What's Coming */}
        <div className="mt-12 grid gap-4 sm:grid-cols-3 text-left">
          <div className="rounded-lg border border-border p-5">
            <h3 className="font-semibold text-sm">Employment Contract Generator</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Create compliant contracts tailored to Irish employment law.
            </p>
          </div>
          <div className="rounded-lg border border-border p-5">
            <h3 className="font-semibold text-sm">Staff Handbook Generator</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Build comprehensive handbooks covering all statutory requirements.
            </p>
          </div>
          <div className="rounded-lg border border-border p-5">
            <h3 className="font-semibold text-sm">Compliance Calendar</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Never miss a deadline with automated compliance reminders.
            </p>
          </div>
        </div>

        {/* Guide Link */}
        <p className="mt-8 text-sm text-muted-foreground">
          <a
            href="/guide/auto-enrolment-pension-ireland"
            className="text-accent hover:underline transition-colors"
          >
            Learn more about pension auto-enrolment &rarr;
          </a>
        </p>

        {/* Pricing Anchor */}
        <p className="mt-10 text-sm text-muted-foreground">
          From <span className="font-semibold text-foreground">€25/month</span>
        </p>

        {/* Social Proof */}
        {waitlistCount !== null && waitlistCount >= 50 && (
          <p className="mt-8 text-sm text-muted-foreground">
            Join {waitlistCount.toLocaleString()} Irish employers waiting for launch
          </p>
        )}

        {/* Waitlist Form */}
        <div className={waitlistCount !== null && waitlistCount >= 50 ? "mt-4" : "mt-8"}>
          {status === "success" ? (
            <p className="text-base font-medium text-foreground">
              You're on the list! We'll email you when Clerq launches.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="you@company.ie"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === "error") {
                    setStatus("idle");
                    setErrorMessage("");
                  }
                }}
                className="flex-1 rounded-lg border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                required
              />
              <button
                type="submit"
                disabled={status === "submitting"}
                className="rounded-lg bg-accent text-accent-foreground px-6 py-2.5 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {status === "submitting" ? "Joining..." : "Join the Waitlist"}
              </button>
            </form>
          )}
          {status === "error" && errorMessage && (
            <p className="mt-3 text-sm text-destructive">{errorMessage}</p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center text-sm text-muted-foreground pb-8">
        &copy; {new Date().getFullYear()} Clerq
      </footer>
    </div>
  );
}

export default App;
